import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, X } from 'lucide-react';

import { Button } from '../../../components/ui/button';

/**
 * Inline help drawer for the Surveys module.
 * Triggered by the small "Help" button in the top-right of the Surveys list page.
 * All copy is bilingual via i18n.
 */
export const SurveyHelpButton = ({ onStartGuidedSetup }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    return (
        <>
            <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpen(true)}
                data-testid="surveys-help-btn"
            >
                <HelpCircle className="w-4 h-4 mr-1.5" />
                {t('surveysHelp.button')}
            </Button>

            {open && (
                <div className="fixed inset-0 z-50 flex justify-end">
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={() => setOpen(false)}
                    />
                    <div className="relative h-full w-full max-w-md bg-background dark:bg-slate-900 shadow-xl overflow-y-auto p-6 space-y-5" data-testid="surveys-help-panel">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {t('surveysHelp.title')}
                            </h2>
                            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                                <X className="w-5 h-5" />
                            </Button>
                        </div>
                        <p className="text-sm text-muted-foreground">{t('surveysHelp.intro')}</p>

                        {onStartGuidedSetup && (
                            <Button
                                onClick={() => { setOpen(false); onStartGuidedSetup(); }}
                                className="w-full"
                                data-testid="help-start-guided-btn"
                            >
                                {t('surveys.startGuidedSetup')}
                            </Button>
                        )}

                        <Section title={t('surveysHelp.s1Title')} body={t('surveysHelp.s1Body')} />
                        <Section title={t('surveysHelp.s2Title')} body={t('surveysHelp.s2Body')} />
                        <Section title={t('surveysHelp.s3Title')} body={t('surveysHelp.s3Body')} />
                        <Section title={t('surveysHelp.s4Title')} body={t('surveysHelp.s4Body')} />
                        <Section title={t('surveysHelp.s5Title')} body={t('surveysHelp.s5Body')} />
                        <Section title={t('surveysHelp.s6Title')} body={t('surveysHelp.s6Body')} />

                        <div className="border-t border-slate-200 dark:border-slate-700 pt-4 text-xs text-muted-foreground">
                            {t('surveysHelp.contactNote')}{' '}
                            <a href="mailto:support@yulontap.com" className="text-fuchsia-600 dark:text-fuchsia-400 underline">
                                support@yulontap.com
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const Section = ({ title, body }) => (
    <div className="space-y-1">
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground whitespace-pre-line">{body}</p>
    </div>
);

export default SurveyHelpButton;
